import React, { useCallback, useContext, useEffect, useState } from "react";
import { PlainCard } from "@/components/settings_components";
import { Form, Formik, type FormikHelpers } from "formik";
import { boolean, number, object, string, ValidationError } from "yup";
import { companyDataAtom } from "../../test-run/test-run-atoms";
import { trackSentryException } from "@/utils/helpers";
import { useAtomValue, useSetAtom } from "jotai";
import { AuthContext } from "@/auth/AuthContext";
import { doc, onSnapshot, updateDoc } from "@firebase/firestore";
import { customFirestore } from "@/firebase";
import { customAlertStateAtom } from "@/components/custom-alert";
import FormikTextInput from "../../../formikComponents/formik-text-input";
import { Button, CircularProgress } from "@mui/material";
import GitHubIcon from "@mui/icons-material/GitHub";
import { Check } from "@mui/icons-material";
import FormikSwitch from "../../../formikComponents/formik-switch";
import { useQueryClient } from "@tanstack/react-query";
import { type CompanyData } from "@/utils/types";
import FirebaseAppDistributionIntegration from "@/routes/settings/integrations/app-distribution";

function useGithubAppInstalled(
  organisationId: string | undefined,
  initialValue: boolean
) {
  const [installed, setInstalled] = useState<boolean>(initialValue);

  useEffect(() => {
    if (!organisationId) return;

    const settingsRef = doc(customFirestore, "settings", organisationId);
    const unsubscribe = onSnapshot(settingsRef, (snap) => {
      const data = snap.data();
      const installations: string[] = data?.github_app_installations ?? [];
      setInstalled(installations.length > 0);
    });

    return unsubscribe;
  }, [organisationId]);

  return installed;
}

const IntegrationsConfigs = () => {
  const { user } = useContext<any>(AuthContext);
  const {
    data: companyData,
    isLoading,
    error,
    isError,
  } = useAtomValue(companyDataAtom(user.companyId));
  const settingsData = companyData?.settings;
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const queryClient = useQueryClient();
  const githubAppInstalled = useGithubAppInstalled(
    user.companyId,
    settingsData?.githubAppInstalled ?? false
  );

  const openGithubAppPopup = useCallback(() => {
    const url = `https://github.com/apps/mobileboost-test-agent/installations/new?state=${user.companyId}`;
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      url,
      "github-app-install",
      `popup,width=${width},height=${height},left=${left},top=${top}`
    );
    if (!popup || popup.closed) {
      window.open(url, "_blank");
    }
  }, [user.companyId]);

  return (
    <PlainCard>
      {isLoading && (
        <div
          className={"flex flex-col h-full items-center justify-center w-full"}
        >
          <CircularProgress />
        </div>
      )}
      {isError && (
        <div className={"mt-4 flex justify-center items-center h-full"}>
          <p className=" font-semibold text-red-400">
            An error occurred while fetching settings: {(error as any)?.message}
          </p>
        </div>
      )}
      {!isLoading && !isError && settingsData && (
        <Formik
          initialValues={{
            ...(settingsData.displayJiraIntegration && {
              jiraIntegration: settingsData.jiraIntegration,
            }),
            ...(settingsData.displayBrowserStackIntegration && {
              browserStackIntegration: settingsData.browserStackIntegration,
            }),
          }}
          validationSchema={object()
            .shape({
              jiraIntegration: object({
                enabled: boolean().default(false),
                host: string().nonNullable().default(""),
                email: string()
                  .email("Invalid email format")
                  .nonNullable()
                  .default(""),
                apiToken: string().nonNullable().default(""),
                projectKey: string().nonNullable().default(""),
              }).nullable(),
              browserStackIntegration: object({
                username: string().default(""),
                accessKey: string().default(""),
                parallelDevices: number()
                  .moreThan(0, "Set at least 1 device")
                  .default(1),
              }).nullable(),
            })
            .test(
              "jira-required-if-enabled",
              "All values are required when the integration is enabled",
              function (values, context) {
                if (!settingsData.displayJiraIntegration) return true;

                const { enabled, ...rest } = values.jiraIntegration!;
                if (enabled) {
                  const validationErrors = [];

                  for (const key of Object.keys(rest)) {
                    if (rest[key]?.trim()?.length === 0) {
                      validationErrors.push(
                        context.createError({
                          path: `jiraIntegration.${key}`,
                          message: `Required Field`,
                        })
                      );
                    }
                  }

                  if (validationErrors.length > 0)
                    return new ValidationError(validationErrors);
                }

                return true;
              }
            )
            .test(
              "browser-stack-required-if-displayed",
              function (values, context) {
                if (!settingsData.displayBrowserStackIntegration) return true;

                const { accessKey, username } = values.browserStackIntegration!;
                const validAccessKey = accessKey?.trim()?.length > 0;
                const validUsername = username?.trim()?.length > 0;

                if (
                  (validAccessKey && !validUsername) ||
                  (!validAccessKey && validUsername)
                ) {
                  return new ValidationError(
                    "Both username and access key are required",
                    null,
                    validAccessKey
                      ? "browserStackIntegration.username"
                      : "browserStackIntegration.accessKey"
                  );
                }
                return true;
              }
            )}
          onSubmit={async (values, formikHelpers: FormikHelpers<any>) => {
            try {
              const settingsRef = doc(
                customFirestore,
                "settings",
                user.companyId
              );

              const newData = {
                ...(values.jiraIntegration && {
                  jiraIntegration: values.jiraIntegration,
                }),
                ...(values.browserStackIntegration && {
                  browserStackIntegration: values.browserStackIntegration,
                }),
              };
              await updateDoc(settingsRef, newData);
              queryClient.setQueryData(
                ["getCompanyData", companyData.organisationId],
                (oldData: CompanyData) => ({
                  ...oldData,
                  settings: {
                    ...oldData.settings,
                    ...newData,
                  },
                })
              );
              companyDataAtom.remove(companyData?.organisationId);
              setCustomAlertState({
                open: true,
                type: "success",
                title: "Success",
                description: "Integrations settings saved successfully",
              });
              formikHelpers.resetForm({ values });
            } catch (error: any) {
              trackSentryException(error);
              console.error(
                "An error occurred during Integrations settings saving",
                error
              );
              setCustomAlertState({
                open: true,
                type: "error",
                title: "Something went wrong",
                description:
                  "An error occurred while saving Integrations settings",
              });
            }
          }}
        >
          {({ dirty, isSubmitting, values }) => (
            <Form className={"h-full"}>
              <div className={"flex justify-between"}>
                <p className="text-3xl pl-4 font-bold">Integrations</p>
                <Button
                  variant="outlined"
                  size={"large"}
                  type="submit"
                  disabled={!dirty}
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </div>
              <div className="flex flex-col gap-6 pt-4 relative sm:rounded-lg h-[calc(100%-50.25px)] overflow-auto">
                {settingsData?.displayGithubIntegration && (
                  <div className="border border-gray-200 rounded-lg p-6 bg-white">
                    <div className="flex gap-2 items-center mb-3">
                      <GitHubIcon />
                      <p className="text-xl font-semibold">GitHub</p>
                    </div>
                    {githubAppInstalled ? (
                      <>
                        <p className="text-sm text-green-600 mb-4 flex items-center gap-1">
                          <Check fontSize="small" />
                          MobileBoost Test Agent app installed for your organization
                        </p>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outlined"
                            startIcon={<GitHubIcon />}
                            onClick={openGithubAppPopup}
                          >
                            Configure
                          </Button>
                          <p className="text-xs text-gray-400 max-w-xs">
                            You will need the correct access rights to configure the app. Insufficient permissions will result in a 404 page.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-500 mb-4">
                          Install the MobileBoost Test Agent to enable automated
                          test reporting on your pull requests.
                        </p>
                        <Button
                          variant="outlined"
                          startIcon={<GitHubIcon />}
                          onClick={openGithubAppPopup}
                        >
                          Install GitHub App
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {settingsData.displayJiraIntegration && (
                  <div className="border border-gray-200 rounded-lg p-6 bg-white">
                    <div className="flex gap-2 items-center mb-4">
                      <p className="text-xl font-semibold">Jira</p>
                    </div>
                    <div className="flex flex-col max-w-lg gap-4">
                      <FormikSwitch
                        type={"checkbox"}
                        label={"Enabled"}
                        name={"jiraIntegration.enabled"}
                        key={`jiraIntegration.enabled`}
                      />
                      <FormikTextInput
                        name={`jiraIntegration.host`}
                        label={"Host"}
                        placeholder={"Host"}
                        type={"text"}
                        disabled={!values.jiraIntegration.enabled}
                        required
                      />
                      <FormikTextInput
                        name={`jiraIntegration.email`}
                        label={"Email"}
                        placeholder={"Email"}
                        type={"text"}
                        disabled={!values.jiraIntegration.enabled}
                        required
                      />
                      <FormikTextInput
                        name={`jiraIntegration.apiToken`}
                        label={"API Token"}
                        placeholder={"API Token"}
                        type={"text"}
                        disabled={!values.jiraIntegration.enabled}
                        required
                      />
                      <FormikTextInput
                        name={`jiraIntegration.projectKey`}
                        label={"Project Key"}
                        placeholder={"Project Key"}
                        type={"text"}
                        disabled={!values.jiraIntegration.enabled}
                        required
                      />
                    </div>
                  </div>
                )}
                {settingsData?.displayBrowserStackIntegration && (
                  <div className="border border-gray-200 rounded-lg p-6 bg-white">
                    <div className="flex gap-2 items-center mb-4">
                      <p className="text-xl font-semibold">BrowserStack</p>
                    </div>
                    <div className="flex flex-col max-w-lg gap-4">
                      <FormikTextInput
                        name={`browserStackIntegration.username`}
                        label={"Username"}
                        placeholder={"Username"}
                        type={"text"}
                      />
                      <FormikTextInput
                        name={`browserStackIntegration.accessKey`}
                        label={"Access Key"}
                        placeholder={"Access Key"}
                        type={"text"}
                      />
                      <FormikTextInput
                        name={`browserStackIntegration.parallelDevices`}
                        label={"Parallel Devices"}
                        placeholder={"Parallel Devices"}
                        type={"number"}
                      />
                    </div>
                  </div>
                )}
                {companyData?.settings.enableAppDistributionIntegration && (
                  <div className="border border-gray-200 rounded-lg p-6 bg-white">
                    <FirebaseAppDistributionIntegration
                      companyData={companyData}
                    />
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      )}
    </PlainCard>
  );
};

export default IntegrationsConfigs;
